import { Injectable, ElementRef } from '@angular/core';
import {
  ChildTableColumn,
  ChildTableConfig,
  ChildTableRow,
  CellPosition,
  normalizeRange,
  NAVIGATION_KEYS,
} from '../models/child-table.models';
import { ChildTableStateService } from './child-table-state.service';
import { ChildTableA11yService } from './child-table-a11y.service';
import { ChildTableEditingService } from './child-table-editing.service';
import { ChildTableSelectionService } from './child-table-selection.service';

/**
 * Runtime context passed from the component to keyboard handler methods.
 * Provides access to component-level data and callbacks that can't be injected.
 */
export interface KeyboardContext<T = any> {
  displayRows: ChildTableRow<T>[];
  visibleRows: ChildTableRow<T>[];
  columns: ChildTableColumn<T>[];
  visibleColumns: ChildTableColumn<T>[];
  config: ChildTableConfig<T>;
  hiddenColumns: Set<string>;
  tableElement: ElementRef<HTMLElement>;
  isMac: boolean;
  pasteErrorLocked: boolean;
  onSort: (columnKey: string) => void;
  onAddRow: () => void;
  onDeleteSelected: () => void;
  onSelectAll: () => void;
  onRowSelect: (rowId: string) => void;
  onMoveRowUp: (rowId: string, event?: KeyboardEvent) => void;
  onMoveRowDown: (rowId: string, event?: KeyboardEvent) => void;
  onManualSave: () => void;
  onCopyAction: () => void;
  onCutAction: () => void;
  onClearCells: () => void;
  undoBadPaste: () => void;
  toggleColumnMenu: (columnKey: string, event: Event) => void;
  openRowMenuWithTrigger: (rowId: string, rowIndex: number, trigger: HTMLElement) => void;
  startEditing: (rowId: string, columnKey: string, event?: Event) => void;
  saveEditQuietly: (skipDomSync?: boolean) => boolean;
  saveEdit: () => void;
  cancelEdit: () => void;
  clearCellRange: () => void;
  showReadonlyFieldAlert: (label: string) => void;
  onInlineCheckboxToggle: (value: boolean, rowId: string, columnKey: string) => void;
  onFileRemove: (rowId: string, columnKey: string, event: Event) => void;
  getCellValue: (row: ChildTableRow<T>, columnKey: string) => any;
  formatCellValueForA11y: (value: any, column: ChildTableColumn<T>) => string;
  markForCheck: () => void;
  isEditable: boolean;
  focusAddRowButton: () => boolean;
}

/**
 * Manages keyboard navigation, focus management, and header interaction mode
 * for the child table grid.
 *
 * Component-scoped (provided via the host component's `providers` array).
 */
@Injectable()
export class ChildTableKeyboardService<T = any> {
  focusedCell: CellPosition | null = null;
  headerInteractionMode = false;
  headerInteractionColIndex: number | null = null;
  _programmaticFocusActive = false;

  private menuOpenedFromInteractionMode = false;
  private lastFocusedRowIndex = -999;

  constructor(
    private readonly stateService: ChildTableStateService<T>,
    private readonly a11yService: ChildTableA11yService,
    private readonly editingService: ChildTableEditingService<T>,
    private readonly selectionService: ChildTableSelectionService,
  ) {}

  // ============================================================
  // Focus management
  // ============================================================

  private getCellSelector(position: CellPosition): string {
    if (position.rowIndex === -1) {
      if (position.columnIndex === -1) return '.grid-header .row-select-col';
      if (position.columnIndex === -2) return '.grid-header .actions-col';
      return `.grid-header .grid-cell[data-col-index="${position.columnIndex}"]`;
    }
    if (position.columnIndex === -1) return `.grid-cell.row-select-col[data-row-index="${position.rowIndex}"]`;
    if (position.columnIndex === -2) return `.grid-cell.actions-col[data-row-index="${position.rowIndex}"]`;
    return `.grid-cell[data-row-index="${position.rowIndex}"][data-col-index="${position.columnIndex}"]`;
  }

  focusCellByPosition(position: CellPosition, tableElement: ElementRef<HTMLElement>): void {
    this.focusedCell = position;
    if (!tableElement?.nativeElement) return;

    const cell = tableElement.nativeElement.querySelector(this.getCellSelector(position)) as HTMLElement;
    if (cell) {
      this.updateRovingTabindex(cell, tableElement);
      this._programmaticFocusActive = true;
      cell.focus();
      Promise.resolve().then(() => { this._programmaticFocusActive = false; });
      cell.scrollIntoView({ block: 'nearest' });
      this.announceFocusedCell(position, null);
    }
  }

  /**
   * Overload that accepts the full context for cell announcements.
   */
  focusCellByPositionWithContext(position: CellPosition, ctx: KeyboardContext<T>): void {
    this.focusedCell = position;
    if (!ctx.tableElement?.nativeElement) return;

    const cell = ctx.tableElement.nativeElement.querySelector(this.getCellSelector(position)) as HTMLElement;
    if (cell) {
      this.updateRovingTabindex(cell, ctx.tableElement);
      this._programmaticFocusActive = true;
      cell.focus();
      Promise.resolve().then(() => { this._programmaticFocusActive = false; });
      cell.scrollIntoView({ block: 'nearest' });
      this.announceFocusedCell(position, ctx);
    }
  }

  updateRovingTabindex(newCell: HTMLElement, tableElement: ElementRef<HTMLElement>): void {
    if (!tableElement?.nativeElement) return;
    const isHeader = !!newCell.closest('.grid-header');
    const scope = isHeader ? '.grid-header' : '.grid-body';
    const prevCells = tableElement.nativeElement.querySelectorAll(
      `${scope} [tabindex="0"]`
    );
    prevCells.forEach((el: Element) => {
      if (el !== newCell) el.setAttribute('tabindex', '-1');
    });
    newCell.setAttribute('tabindex', '0');
  }

  resetRovingTabindex(tableElement: ElementRef<HTMLElement>): void {
    if (!tableElement?.nativeElement) return;
    const grid = tableElement.nativeElement;
    grid.querySelectorAll('.grid-cell[tabindex="0"]').forEach((el: Element) => {
      el.setAttribute('tabindex', '-1');
    });
    const headerEntry = grid.querySelector(
      '.grid-header .actions-col, .grid-header .column-header'
    ) as HTMLElement;
    if (headerEntry) headerEntry.setAttribute('tabindex', '0');
    const bodyEntry = grid.querySelector(
      '.grid-cell[data-row-index="0"][data-col-index="0"]'
    ) as HTMLElement;
    if (bodyEntry) bodyEntry.setAttribute('tabindex', '0');
  }

  announceFocusedCell(position: CellPosition, ctx: KeyboardContext<T> | null): void {
    const { rowIndex, columnIndex } = position;
    const visibleColumns = ctx?.visibleColumns ?? [];
    const visibleRows = ctx?.visibleRows ?? [];

    if (rowIndex === -1) {
      let label: string;
      if (columnIndex === -1) {
        label = 'Row selection. Press Space to select all rows.';
      } else if (columnIndex === -2) {
        label = 'Actions column header.';
      } else {
        const col = visibleColumns[columnIndex];
        const colName = col?.label || '';
        const sortHint = col?.sortable ? ' Sortable. Press Enter to sort, Space for column actions.' : '';
        label = `${colName}, column ${columnIndex + 1} of ${visibleColumns.length}.${sortHint}`;
      }
      if (this.lastFocusedRowIndex >= 0) {
        label = `Header row. ${label} Use arrows to navigate columns.`;
      }
      this.a11yService.announce(label);
    } else if (rowIndex >= 0) {
      if (this.lastFocusedRowIndex !== rowIndex) {
        const col = columnIndex >= 0 ? visibleColumns[columnIndex] : null;
        const colName = col?.label || '';
        const row = visibleRows[rowIndex];
        const cellValue = row && col && ctx
          ? ctx.formatCellValueForA11y(ctx.getCellValue(row, col.key), col)
          : '';
        const valuePart = cellValue ? `, ${cellValue}` : '';
        const colPart = colName ? `, ${colName} column` : '';
        this.a11yService.announce(`Row ${rowIndex + 1}${valuePart}${colPart}`);
      }
    }
    this.lastFocusedRowIndex = rowIndex;
  }

  /** Reset lastFocusedRowIndex (e.g. on page change). */
  resetLastFocusedRowIndex(value = -999): void {
    this.lastFocusedRowIndex = value;
  }

  // ============================================================
  // Header interaction mode (WAI-ARIA "Editing Inside a Cell")
  // ============================================================

  enterHeaderInteractionMode(colIndex: number, ctx: KeyboardContext<T>): void {
    if (!ctx.tableElement?.nativeElement) return;
    const th = ctx.tableElement.nativeElement.querySelector(
      `.grid-header .grid-cell[data-col-index="${colIndex}"]`
    ) as HTMLElement;
    if (!th) return;

    const widgets = this.getHeaderWidgets(th);
    if (widgets.length === 0) return;

    this.headerInteractionMode = true;
    this.headerInteractionColIndex = colIndex;

    const col = ctx.visibleColumns[colIndex];
    this.a11yService.announceHeaderInteractionMode(col?.label || '');
    ctx.markForCheck();

    widgets[0].focus();
  }

  exitHeaderInteractionMode(ctx: KeyboardContext<T>): void {
    const colIndex = this.headerInteractionColIndex;
    this.headerInteractionMode = false;
    this.headerInteractionColIndex = null;
    this.a11yService.announceHeaderInteractionExit();
    ctx.markForCheck();

    if (colIndex !== null) {
      this.focusCellByPositionWithContext({ rowIndex: -1, columnIndex: colIndex }, ctx);
    }
  }

  getHeaderWidgets(th: HTMLElement): HTMLElement[] {
    const selectors = ['.sort-btn', '.column-menu-btn', '.col-resize-handle'];
    const widgets: HTMLElement[] = [];
    for (const sel of selectors) {
      const el = th.querySelector(sel) as HTMLElement;
      if (el) widgets.push(el);
    }
    return widgets;
  }

  onHeaderWidgetKeydown(event: KeyboardEvent, columnKey: string, colIndex: number, widgetType: string, ctx: KeyboardContext<T>): void {
    if (event.key === NAVIGATION_KEYS.ESCAPE) {
      event.preventDefault();
      event.stopPropagation();
      this.exitHeaderInteractionMode(ctx);
      return;
    }

    if (event.key === NAVIGATION_KEYS.ENTER || event.key === NAVIGATION_KEYS.SPACE) {
      event.stopPropagation();
      return;
    }

    if (event.key === NAVIGATION_KEYS.TAB) {
      if (!this.headerInteractionMode) return;
      event.preventDefault();
      event.stopPropagation();

      const th = ctx.tableElement?.nativeElement?.querySelector(
        `.grid-header .grid-cell[data-col-index="${colIndex}"]`
      ) as HTMLElement;
      if (!th) return;

      const widgets = this.getHeaderWidgets(th);
      if (widgets.length <= 1) return;

      const current = document.activeElement as HTMLElement;
      const idx = widgets.indexOf(current);
      if (idx === -1) return;

      const next = event.shiftKey
        ? (idx - 1 + widgets.length) % widgets.length
        : (idx + 1) % widgets.length;
      widgets[next].focus();
    }
  }

  returnFocusAfterMenuClose(ctx: KeyboardContext<T>): void {
    if (this.menuOpenedFromInteractionMode && this.headerInteractionColIndex !== null) {
      const th = ctx.tableElement?.nativeElement?.querySelector(
        `.grid-header .grid-cell[data-col-index="${this.headerInteractionColIndex}"] .column-menu-btn`
      ) as HTMLElement;
      this.menuOpenedFromInteractionMode = false;
      if (th) {
        th.focus();
        return;
      }
    }
    if (this.headerInteractionColIndex !== null) {
      this.exitHeaderInteractionMode(ctx);
    }
  }

  /** Mark that a menu was opened from interaction mode (for focus restoration). */
  setMenuOpenedFromInteractionMode(value: boolean): void {
    this.menuOpenedFromInteractionMode = value;
  }

  get isMenuOpenedFromInteractionMode(): boolean {
    return this.menuOpenedFromInteractionMode;
  }

  // ============================================================
  // Global keyboard handler
  // ============================================================

  onKeydown(event: KeyboardEvent, ctx: KeyboardContext<T>): void {
    if ((event.target as HTMLElement)?.closest('.column-menu, child-table-row-menu')) return;
    if (this.a11yService.isUndoShortcut(event)) {
      event.preventDefault();
      if (ctx.pasteErrorLocked) { ctx.undoBadPaste(); }
      return;
    }
    if (ctx.pasteErrorLocked) {
      event.preventDefault();
      return;
    }
    if (this.a11yService.isSelectAllShortcut(event)) {
      event.preventDefault();
      ctx.onSelectAll();
      return;
    }
    if (this.a11yService.isAddRowShortcut(event)) {
      event.preventDefault();
      ctx.onAddRow();
      return;
    }
    if (this.a11yService.isDeleteRowShortcut(event)) {
      event.preventDefault();
      ctx.onDeleteSelected();
      return;
    }
    if (this.a11yService.isCopyShortcut(event)) {
      event.preventDefault();
      ctx.onCopyAction();
      return;
    }
    if (this.a11yService.isCutShortcut(event)) {
      event.preventDefault();
      ctx.onCutAction();
    }
  }

  // ============================================================
  // Cell-level keyboard handler
  // ============================================================

  onCellKeydown(event: KeyboardEvent, rowId: string, columnKey: string, rowIndex: number, colIndex: number, ctx: KeyboardContext<T>): void {
    if (ctx.pasteErrorLocked) { event.preventDefault(); return; }

    // Enter/Space on actions cell: open row menu
    if (colIndex === -2 && (event.key === NAVIGATION_KEYS.ENTER || event.key === NAVIGATION_KEYS.SPACE)) {
      event.preventDefault();
      const trigger = ctx.tableElement?.nativeElement?.querySelector(
        `.grid-cell.actions-col[data-row-id="${rowId}"] .kebab-btn`
      ) as HTMLElement | null;
      if (trigger) {
        ctx.openRowMenuWithTrigger(rowId, rowIndex, trigger);
      }
      return;
    }

    // Shift+F10 or Ctrl+M: open row actions menu
    if ((event.shiftKey && event.key === 'F10') ||
        (event.ctrlKey && !event.shiftKey && !event.metaKey && event.key.toLowerCase() === 'm')) {
      event.preventDefault();
      const trigger = ctx.tableElement?.nativeElement?.querySelector(
        `.grid-cell.actions-col[data-row-id="${rowId}"] .kebab-btn`
      ) as HTMLElement | null;
      if (trigger) {
        ctx.openRowMenuWithTrigger(rowId, rowIndex, trigger);
      }
      return;
    }

    // --- Row move shortcuts (Cmd+Shift+Arrow) ---
    if (this.a11yService.isMoveRowUpShortcut(event) || this.a11yService.isMoveRowDownShortcut(event)) {
      if (this.selectionService.cellRange) {
        const { minRow, maxRow, minCol, maxCol } = normalizeRange(this.selectionService.cellRange);
        if (minRow !== maxRow || minCol !== maxCol) {
          event.preventDefault();
          this.a11yService.announce('Clear cell selection first to move row', 'assertive');
          return;
        }
      }
      if (this.editingService.editingCell) {
        if (!ctx.saveEditQuietly()) {
          event.preventDefault();
          return;
        }
      }
      if (this.a11yService.isMoveRowUpShortcut(event)) {
        ctx.onMoveRowUp(rowId, event);
      } else {
        ctx.onMoveRowDown(rowId, event);
      }
      return;
    }

    // --- Editing mode ---
    if (this.editingService.editingCell) {
      if (event.key === NAVIGATION_KEYS.ENTER) { event.preventDefault(); ctx.saveEdit(); return; }
      if (event.key === NAVIGATION_KEYS.ESCAPE) { event.preventDefault(); ctx.cancelEdit(); return; }
      if (event.key === NAVIGATION_KEYS.TAB) {
        event.preventDefault();
        const gridCell = (event.target as HTMLElement).closest('.grid-cell');
        const inputEl = gridCell?.querySelector('input.cell-input') as HTMLInputElement;
        if (inputEl) {
          const col = ctx.columns.find(c => c.key === this.editingService.editingCell?.columnKey);
          const isNumeric = col?.type === 'number' || col?.type === 'currency';
          this.editingService.editingValue = isNumeric
            ? (inputEl.value === '' ? null : Number(inputEl.value))
            : inputEl.value;
        }
        if (!ctx.saveEditQuietly(!!inputEl)) return;
        const nextCol = event.shiftKey ? colIndex - 1 : colIndex + 1;
        if (nextCol >= 0 && nextCol < ctx.visibleColumns.length) {
          this.focusCellByPositionWithContext({ rowIndex, columnIndex: nextCol }, ctx);
        } else {
          this.focusCellByPositionWithContext({ rowIndex, columnIndex: colIndex }, ctx);
        }
        return;
      }
      return;
    }

    // Escape clears cell range selection
    if (event.key === NAVIGATION_KEYS.ESCAPE) {
      if (this.selectionService.cellRange) { event.preventDefault(); ctx.clearCellRange(); return; }
    }

    // Checkbox: toggle directly with Enter/Space
    if ((this.a11yService.isEditTriggerKey(event.key) || event.key === NAVIGATION_KEYS.SPACE) && colIndex >= 0) {
      const col = ctx.visibleColumns[colIndex];
      if (col?.type === 'checkbox') {
        event.preventDefault();
        const currentRow = ctx.displayRows.find((r) => r.id === rowId);
        const currentVal = currentRow ? (currentRow.data as any)[col.key] : false;
        const s = String(currentVal).toLowerCase();
        const isChecked = currentVal === true || currentVal === 1 || s === '1' || s === 'true';
        ctx.onInlineCheckboxToggle(!isChecked, rowId, col.key);
        this.a11yService.announce(!isChecked ? 'Checked' : 'Unchecked');
        return;
      }
    }

    // Delete/Backspace: remove file cell OR clear selected cell range / selected rows
    if (event.key === 'Delete' || event.key === 'Backspace') {
      // File cell takes priority
      if (colIndex >= 0) {
        const col = ctx.visibleColumns[colIndex];
        if (col?.type === 'file' && col.editable && !ctx.config.readonly) {
          const currentRow = ctx.displayRows.find((r) => r.id === rowId);
          if (currentRow && ctx.getCellValue(currentRow, col.key)) {
            event.preventDefault();
            ctx.onFileRemove(rowId, col.key, event);
            this.a11yService.announce('File removed');
            return;
          }
        }
      }
      // Clear selected cell range or checkbox-selected rows
      const hasCellRange = !!this.selectionService.cellRange;
      const selectedIds = this.stateService.getState().selectedIds;
      if ((hasCellRange || selectedIds.size > 0) && !ctx.config.readonly) {
        event.preventDefault();
        ctx.onClearCells();
        return;
      }
    }

    if (this.a11yService.isEditTriggerKey(event.key)) {
      event.preventDefault();
      ctx.startEditing(rowId, columnKey, event);
      return;
    }
    if (event.key === NAVIGATION_KEYS.SPACE && colIndex === -1) {
      event.preventDefault();
      ctx.onRowSelect(rowId);
      return;
    }
    // Tab: try to focus the Add Row button before exiting the grid entirely
    if (event.key === NAVIGATION_KEYS.TAB) {
      if (!event.shiftKey && ctx.isEditable) {
        const isLastRow = rowIndex === ctx.visibleRows.length - 1;
        const isLastCol = colIndex === ctx.visibleColumns.length - 1 || colIndex < 0;
        if (isLastRow && isLastCol) {
          event.preventDefault();
          ctx.focusAddRowButton();
          return;
        }
      }
      return;
    }
    if (this.a11yService.isNavigationKey(event.key)) {
      const totalDataColumns = ctx.visibleColumns.length;
      const currentPosition: CellPosition = { rowIndex, columnIndex: colIndex };
      const hasRowSelectCol = !!(ctx.config.selectable || ctx.config.rowReorderable);
      const hasActionsCol = ctx.config.showRowActions !== false;
      const ctrlOrMeta = ctx.isMac ? event.metaKey : event.ctrlKey;
      const nextPosition = this.a11yService.getNextCellPosition(
        currentPosition, event.key, ctx.visibleRows.length, totalDataColumns, event.shiftKey, hasRowSelectCol, hasActionsCol, ctrlOrMeta
      );
      if (nextPosition) {
        event.preventDefault();

        if (event.shiftKey && this.isArrowKey(event.key)) {
          if (this.selectionService.cellRange) {
            this.selectionService.cellRange = { ...this.selectionService.cellRange, focusRow: nextPosition.rowIndex, focusCol: nextPosition.columnIndex };
          } else {
            this.selectionService.cellRange = {
              anchorRow: rowIndex, anchorCol: colIndex,
              focusRow: nextPosition.rowIndex, focusCol: nextPosition.columnIndex,
            };
          }
          this.focusedCell = nextPosition;
          this.focusCellByPositionWithContext(nextPosition, ctx);
          this.selectionService.announceRangeIfMultiCell();
          ctx.markForCheck();
        } else {
          this.selectionService.cellRange = {
            anchorRow: nextPosition.rowIndex, anchorCol: nextPosition.columnIndex,
            focusRow: nextPosition.rowIndex, focusCol: nextPosition.columnIndex,
          };
          this.focusCellByPositionWithContext(nextPosition, ctx);
        }
      }
    }
  }

  // ============================================================
  // Header keyboard handler
  // ============================================================

  onHeaderKeydown(event: KeyboardEvent, columnKey: string, colIndex: number, ctx: KeyboardContext<T>): void {
    if ((event.target as HTMLElement)?.closest('.column-menu')) return;
    if (this.headerInteractionMode) return;

    if (event.key === NAVIGATION_KEYS.SPACE && colIndex === -1) {
      event.preventDefault();
      ctx.onSelectAll();
      return;
    }

    if (event.key === NAVIGATION_KEYS.SPACE && columnKey) {
      event.preventDefault();
      this.enterHeaderInteractionMode(colIndex, ctx);
      return;
    }

    if (event.key === NAVIGATION_KEYS.F2 && columnKey) {
      event.preventDefault();
      this.enterHeaderInteractionMode(colIndex, ctx);
      return;
    }

    if (event.key === NAVIGATION_KEYS.ENTER && columnKey) {
      event.preventDefault();
      ctx.onSort(columnKey);
      return;
    }

    if (event.shiftKey && event.key === 'F10' && columnKey) {
      event.preventDefault();
      this.headerInteractionColIndex = colIndex;
      this.menuOpenedFromInteractionMode = false;
      const btn = ctx.tableElement?.nativeElement?.querySelector(
        `.grid-cell[data-col-index="${colIndex}"] .column-menu-btn`
      ) as HTMLElement;
      if (btn) btn.click();
      return;
    }

    if (event.key === NAVIGATION_KEYS.TAB) return;

    if (this.a11yService.isNavigationKey(event.key)) {
      const totalDataColumns = ctx.visibleColumns.length;
      const currentPosition: CellPosition = { rowIndex: -1, columnIndex: colIndex };
      const hasRowSelectCol = !!(ctx.config.selectable || ctx.config.rowReorderable);
      const hasActionsCol = ctx.config.showRowActions !== false;
      const headerCtrlOrMeta = ctx.isMac ? event.metaKey : event.ctrlKey;
      const nextPosition = this.a11yService.getNextCellPosition(
        currentPosition, event.key, ctx.visibleRows.length, totalDataColumns, event.shiftKey, hasRowSelectCol, hasActionsCol, headerCtrlOrMeta
      );
      if (nextPosition) {
        event.preventDefault();
        this.focusCellByPositionWithContext(nextPosition, ctx);
      }
    }
  }

  // ============================================================
  // Utilities
  // ============================================================

  isArrowKey(key: string): boolean {
    return key === NAVIGATION_KEYS.ARROW_UP || key === NAVIGATION_KEYS.ARROW_DOWN
      || key === NAVIGATION_KEYS.ARROW_LEFT || key === NAVIGATION_KEYS.ARROW_RIGHT;
  }
}
